import { Stack } from '@mui/material';
import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import {
  BracketPriceAccordion,
  CampaignPrice,
  PackageInfo,
  PriceDatePicker,
  useBracketPackagePrices,
  useProductCardStore,
} from '@retail/my-assortment/components';
import { SupplierItemCalculation } from '@retail/my-assortment/types';
import { useFetchPriceSuggestionItemPrices } from '@retail/price-suggestion/data-access';
import { PriceSuggestionPrice } from '@retail/price-suggestion/types';
import { PackagesButtonGroup } from '@retail/products/components';
import { useEffect, useMemo, useState } from 'react';
import { PackageCalculusCalculations } from './PackageCalculusCalculations';
import { useDisclosure } from '@shared/hooks';
import { EditBracketPricesDialogContainer } from './CalculusCardActions/EditBracketPricesDialogContainer';
import { useIncludeVatMyAssortment } from '@retail/my-assortment/context';
import { getLLDate } from '@retail/utils';

interface Props {
  assortmentId: number;
  supplierItem: SupplierItemCalculation;
  mgItemNumber: number;
}
export function CalculusCard({ assortmentId, supplierItem, mgItemNumber }: Props) {
  const { includeVat, toggleIncludeVat } = useIncludeVatMyAssortment();
  const { selectedBrand } = useUserBrands();
  const { userCanRead, userCanCreate } = usePermissionsForCurrentUser();
  const showPriceSuggestion = userCanRead('PRICE_CALCULATION__PRICE_SUGGESTIONS');
  const canEditMarkup = userCanCreate('PRICE_CALCULATION__MARKUP');
  const isSalesPriceConditionEnabled = useIsFeatureEnabled('sales-price-condition');

  const { data: priceSuggestionItemPricesDTO = [] } = useFetchPriceSuggestionItemPrices(
    { assortmentId, mgItemNumber, mgSupplierId: supplierItem.supplierNumber },
    { enabled: showPriceSuggestion }
  );
  const priceSuggestionItemPrices = priceSuggestionItemPricesDTO?.map(PriceSuggestionPrice.fromDto);

  const sortedPackageTypes = useMemo(() => supplierItem.getPackageTypes(), [supplierItem]);

  const { selectedPackageType, changeSelectedPackage, resetState } = useProductCardStore();

  const packageSupplierItem = useMemo(
    () => supplierItem.packages.find((pkge) => pkge.packageType === selectedPackageType),
    [selectedPackageType, supplierItem.packages]
  );

  useEffect(() => resetState(), [resetState, supplierItem]);

  const {
    isOpen: isBracketPriceModalOpen,
    onOpen: onOpenBracketPricelModal,
    onClose: onCloseBracketPriceModal,
  } = useDisclosure();

  const [selectedDate, selectDate] = useState<string>(getLLDate(supplierItem.dates[0])!);

  const packagePrices = useBracketPackagePrices(supplierItem.packages ?? [], includeVat);

  return (
    <Stack>
      {selectedBrand.wholesaler && (
        <BracketPriceAccordion
          packagePrices={packagePrices}
          baseUnit={supplierItem.basePriceUnit}
          editable={false}
          onEdit={onOpenBracketPricelModal}
        />
      )}

      <PackagesButtonGroup
        packageTypes={sortedPackageTypes}
        selectedPackage={selectedPackageType}
        setSelectedPackage={changeSelectedPackage}
      />
      <PackageInfo
        basePriceUnit={supplierItem.basePriceUnit}
        unit={packageSupplierItem?.unit ?? '-'}
        gtin={packageSupplierItem?.gtin ?? '-'}
      />

      {packageSupplierItem?.campaignPrices && <CampaignPrice campaignPrices={packageSupplierItem.campaignPrices} />}

      <PriceDatePicker
        priceDates={supplierItem.dates}
        selectedPriceDate={selectedDate}
        selectPriceDate={selectDate}
      />

      {packageSupplierItem && (
        <>
          <PackageCalculusCalculations
            assortmentId={assortmentId}
            packageSupplierItem={packageSupplierItem}
            mgSupplierItemId={supplierItem.mgSupplierItemId}
            selectedDate={selectedDate}
            showPriceSuggestion={showPriceSuggestion}
            canEditMarkup={canEditMarkup}
            isSalesPriceConditionEnabled={isSalesPriceConditionEnabled}
            availablePriceSuggestionItemPrices={priceSuggestionItemPrices}
            mgItemNumber={mgItemNumber}
            supplierNumber={supplierItem.supplierNumber}
            primaryText={supplierItem.primaryText}
            incVat={includeVat}
          />
          {isBracketPriceModalOpen && (
            <EditBracketPricesDialogContainer
              supplierItemPackages={supplierItem.packages}
              basePriceUnit={supplierItem.basePriceUnit}
              selectedSupplierId={supplierItem.supplierNumber}
              mgItemNumber={mgItemNumber}
              includeVat={includeVat}
              toggleIncludeVat={toggleIncludeVat}
              isBracketPriceModalOpen={isBracketPriceModalOpen}
              onCloseBracketPriceModal={onCloseBracketPriceModal}
            />
          )}
        </>
      )}
    </Stack>
  );
}
